import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, StringSelect, cn } from "@tellescope/ui";
import { Plus, Trash2 } from "lucide-react";
import type { ElemMatchCondition, ItemCondition, DataSourceType } from "@tellescope/visibility-schema";
import { ARRAY_DATA_SOURCES, getDataSources } from "./constants";
import { ElemMatchRule } from "./ElemMatchRule";

interface ElemMatchGroupProps {
  condition: ElemMatchCondition;
  depth?: number;
  onUpdateSource: (source: DataSourceType) => void;
  onAddCondition: () => void;
  onUpdateCondition: (conditionIndex: number, updates: Partial<ItemCondition>) => void;
  onRemoveCondition: (conditionIndex: number) => void;
  onRemove: () => void;
  className?: string;
}

const ARRAY_SOURCE_OPTIONS = getDataSources()
  .filter((s) => ARRAY_DATA_SOURCES.includes(s.id))
  .map((s) => ({
    value: s.id,
    label: s.label,
  }));

export const ElemMatchGroup = ({
  condition,
  depth = 0,
  onUpdateSource,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  onRemove,
  className,
}: ElemMatchGroupProps) => {
  const { t } = useTranslation();
  const { source, conditions } = condition.$elemMatch;

  const sourceDef = getDataSources().find((s) => s.id === source);
  const singularLabel = sourceDef?.singularLabel ?? source;

  const handleSourceChange = useCallback(
    (newSource: DataSourceType) => {
      onUpdateSource(newSource);
    },
    [onUpdateSource]
  );

  return (
    <div className={cn("border-l-2 border-purple-500 pl-3", className)}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded">{t("MATCH ITEM")}</span>
        <StringSelect
          value={source}
          onValueChange={handleSourceChange}
          options={ARRAY_SOURCE_OPTIONS}
          placeholder={t("Source")}
          className="w-[160px] h-8 text-xs"
        />
        {depth > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive ml-auto"
            onClick={onRemove}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-2">
        {t("Show if at least one {{source}} matches ALL of the following:", { source: singularLabel })}
      </p>

      <div className="space-y-2">
        {conditions.map((cond, index) => (
          <ElemMatchRule
            key={index}
            condition={cond}
            source={source}
            onChange={(updates) => onUpdateCondition(index, updates)}
            onRemove={() => onRemoveCondition(index)}
          />
        ))}
      </div>

      <div className="flex items-center gap-2 mt-2">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onAddCondition}>
          <Plus className="h-3 w-3 mr-1" />
          {t("Add condition")}
        </Button>
      </div>
    </div>
  );
};
